'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Mic } from 'lucide-react';
import { getSpeakerColor } from '@/lib/speaker-colors';
import { cn } from '@/lib/utils';

interface Turn {
  name: string;
  time: string;
  text: string;
  speaker: number;
}

/**
 * Live transcription demo, Chroma styled: speaker-colored chat bubbles that
 * type themselves in, one turn at a time, then resolve into the ink summary
 * card. Honors prefers-reduced-motion (renders the finished state, no typing).
 */
export default function LiveTranscriptionDemo() {
  const t = useTranslations();

  const turns = useMemo<Turn[]>(
    () => [
      {
        name: t('landing.demo.speaker1Name'),
        time: t('landing.demo.speaker1Time'),
        text: t('landing.demo.speaker1Text'),
        speaker: 0,
      },
      {
        name: t('landing.demo.speaker2Name'),
        time: t('landing.demo.speaker2Time'),
        text: t('landing.demo.speaker2Text'),
        speaker: 1,
      },
      {
        name: t('landing.demo.speaker3Name'),
        time: t('landing.demo.speaker3Time'),
        text: t('landing.demo.speaker3Text'),
        speaker: 2,
      },
    ],
    [t]
  );

  const prefersReducedMotion = useRef(false);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
      setReduced(e.matches);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [visibleTurns, setVisibleTurns] = useState(0); // how many turns have started
  const [wordCount, setWordCount] = useState(0); // words revealed in the current turn
  const [showSummary, setShowSummary] = useState(false);
  const [recording, setRecording] = useState(true);
  const [loopKey, setLoopKey] = useState(0);

  // Single reveal loop. Restarts whenever `loopKey` changes (see loop restart
  // effect below). Skipped entirely when reduced motion is preferred.
  useEffect(() => {
    if (reduced) {
      setVisibleTurns(turns.length);
      setShowSummary(true);
      setRecording(false);
      return;
    }

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    setVisibleTurns(0);
    setWordCount(0);
    setShowSummary(false);
    setRecording(true);

    let turnIndex = 0;
    let word = 0;

    const step = () => {
      if (cancelled) return;
      if (turnIndex >= turns.length) {
        setRecording(false);
        timers.push(setTimeout(() => setShowSummary(true), 400));
        return;
      }
      const words = turns[turnIndex].text.split(' ');
      if (word === 0) setVisibleTurns(turnIndex + 1);
      word += 1;
      setWordCount(word);
      if (word >= words.length) {
        // Leave wordCount at the full length so the completed bubble keeps
        // its text; it only resets when the next turn's first word lands.
        turnIndex += 1;
        word = 0;
        timers.push(setTimeout(step, 550));
      } else {
        timers.push(setTimeout(step, 90));
      }
    };

    timers.push(setTimeout(step, 500));

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [turns, reduced, loopKey]);

  // Once the summary is shown, wait, then bump loopKey to restart the loop.
  useEffect(() => {
    if (reduced || !showSummary) return;
    const restart = setTimeout(() => setLoopKey((k) => k + 1), 3500);
    return () => clearTimeout(restart);
  }, [showSummary, reduced]);

  const revealedText = (turn: Turn, idx: number) => {
    if (reduced) return turn.text;
    if (idx + 1 < visibleTurns) return turn.text; // fully revealed earlier turn
    if (idx + 1 === visibleTurns) {
      const words = turn.text.split(' ');
      return words.slice(0, Math.max(wordCount, 1)).join(' ');
    }
    return '';
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-float">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-background px-6 py-4">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground',
              recording && !reduced && 'animate-pulse'
            )}
          >
            <Mic className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {t('landing.demo.title')}
            </h3>
            <p className="flex items-center gap-1.5 text-xs text-secondary-foreground">
              <span
                className={cn(
                  'inline-block h-2 w-2 rounded-full',
                  recording ? 'bg-speaker-4' : 'bg-muted-foreground',
                  recording && !reduced && 'animate-pulse-dot'
                )}
              />
              {recording ? t('landing.demo.recording') : t('landing.demo.paused')}
            </p>
          </div>
        </div>
        <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-secondary-foreground">
          {t('landing.demo.aiProcessing')}
        </span>
      </div>

      {/* Conversation */}
      <div className="grid min-h-[280px] gap-3 p-6" aria-live="polite">
        {turns.map((turn, idx) => {
          const started = reduced || idx + 1 <= visibleTurns;
          if (!started) return null;
          const c = getSpeakerColor(turn.speaker);
          const text = revealedText(turn, idx);
          const isTyping =
            !reduced && idx + 1 === visibleTurns && !showSummary && recording;
          return (
            <div
              key={idx}
              className="grid grid-cols-[34px_1fr] items-start gap-3"
            >
              <span
                className={cn(
                  'grid h-[34px] w-[34px] place-items-center rounded-full text-xs font-extrabold text-white',
                  c.bgClass
                )}
                aria-hidden="true"
              >
                {turn.name.charAt(0)}
              </span>
              <div
                className={cn(
                  'rounded-[4px_16px_16px_16px] border-l-[3px] bg-background px-4 py-2.5 shadow-float',
                  c.borderClass
                )}
              >
                <div className="flex items-baseline gap-2">
                  <span className={cn('text-[11.5px] font-bold', c.textClass)}>
                    {turn.name}
                  </span>
                  <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground">
                    {turn.time}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-[hsl(213_20%_28%)] dark:text-foreground/80">
                  {text}
                  {isTyping && (
                    <span className="ml-0.5 inline-block h-4 w-0.5 -translate-y-0.5 animate-pulse bg-foreground align-middle" />
                  )}
                </p>
              </div>
            </div>
          );
        })}

        {/* Notiia summary card */}
        {showSummary && (
          <div className="animate-fade-in-up mt-1 rounded-2xl bg-primary px-5 py-4 text-sm text-primary-foreground">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-speaker-3">
              {t('landing.demo.summaryLabel')}
            </span>
            {t('landing.demo.summaryBody')}
          </div>
        )}
      </div>
    </div>
  );
}
